import React from 'react';
import { AlertTriangle, CheckCircle2, Wrench, Clock, User, Thermometer } from 'lucide-react';
import { Button } from '../ui/button';

const formatTimestamp = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-NO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Renders the traceability warning banner.
 * When the warning is unresolved → amber banner with "Log manual correction".
 * When resolved → emerald banner showing audit metadata; original gap context
 * is preserved (not deleted).
 */
const WarningBanner = ({ warning, onResolveClick, testIdPrefix = 'optimar-warning' }) => {
  if (!warning) return null;
  const resolved = !!warning.resolved;

  if (resolved) {
    return (
      <div
        data-testid={`${testIdPrefix}-resolved`}
        className="rounded-2xl border border-emerald-300 bg-emerald-50 p-4 sm:p-6"
      >
        <div className="flex items-start gap-3 sm:gap-4">
          <div className="rounded-lg bg-emerald-100 p-2 sm:p-3 shrink-0">
            <CheckCircle2 className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-700" />
          </div>
          <div className="flex-1 space-y-3 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full border border-emerald-300 bg-emerald-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wider text-emerald-800">
                Resolved
              </span>
              <h2 className="text-base sm:text-lg font-semibold text-emerald-900 break-words">
                {warning.title}
              </h2>
            </div>

            <p className="text-sm leading-relaxed text-emerald-900/90">
              {warning.description}
            </p>

            <div className="grid grid-cols-1 gap-3 rounded-lg border border-emerald-200 bg-white p-4 text-sm sm:grid-cols-2">
              <div className="flex items-start gap-2">
                <User className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
                <div>
                  <p className="text-xs uppercase tracking-wider text-emerald-700">Corrected by</p>
                  <p className="font-medium text-slate-900" data-testid={`${testIdPrefix}-resolved-by`}>
                    {warning.resolved_by || '—'}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Clock className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
                <div>
                  <p className="text-xs uppercase tracking-wider text-emerald-700">Timestamp</p>
                  <p className="font-medium text-slate-900" data-testid={`${testIdPrefix}-resolved-at`}>
                    {formatTimestamp(warning.resolved_at)}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2 sm:col-span-2">
                <Wrench className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs uppercase tracking-wider text-emerald-700">Affected production step</p>
                  <p className="font-medium text-slate-900" data-testid={`${testIdPrefix}-resolved-step`}>
                    {warning.affected_step || '—'}
                  </p>
                </div>
              </div>
              {warning.temperature_celsius != null && (
                <div className="flex items-start gap-2 sm:col-span-2">
                  <Thermometer className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
                  <div>
                    <p className="text-xs uppercase tracking-wider text-emerald-700">Temperature recorded</p>
                    <p
                      className="font-medium text-slate-900"
                      data-testid={`${testIdPrefix}-resolved-temperature`}
                    >
                      {Number(warning.temperature_celsius).toFixed(1)} °C
                    </p>
                  </div>
                </div>
              )}
              <div className="sm:col-span-2">
                <p className="text-xs uppercase tracking-wider text-emerald-700">Correction reason</p>
                <p className="mt-0.5 text-sm text-slate-900" data-testid={`${testIdPrefix}-resolved-reason`}>
                  {warning.resolution_reason || '—'}
                </p>
              </div>
              {warning.correction_id && (
                <div className="sm:col-span-2">
                  <p className="text-xs text-slate-500">
                    Correction ID:{' '}
                    <span className="font-mono">{warning.correction_id}</span>
                  </p>
                </div>
              )}
            </div>

            <p className="text-xs italic text-emerald-900/80">
              The original warning is preserved here as part of the audit
              history and is no longer counted in active alerts.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Unresolved (open) warning
  return (
    <div
      data-testid={`${testIdPrefix}-open`}
      className="rounded-2xl border border-amber-300 bg-amber-50 p-4 sm:p-6"
    >
      <div className="flex items-start gap-3 sm:gap-4">
        <div className="rounded-lg bg-amber-100 p-2 sm:p-3 shrink-0">
          <AlertTriangle className="h-5 w-5 sm:h-6 sm:w-6 text-amber-700" />
        </div>
        <div className="flex-1 space-y-2 min-w-0">
          <h2 className="text-base sm:text-lg font-semibold text-amber-900 break-words">{warning.title}</h2>
          <p className="text-sm leading-relaxed text-amber-900/90">
            {warning.description}
          </p>
          <div className="flex flex-wrap gap-2 pt-1 text-xs">
            <span className="rounded-full bg-amber-100 px-2 py-1 text-amber-900">
              Expected: {warning.expected_event}
            </span>
            <span className="break-all rounded-full bg-amber-100 px-2 py-1 text-amber-900">
              Gap: {warning.gap_between?.join(' → ')}
            </span>
            <span className="rounded-full bg-amber-100 px-2 py-1 text-amber-900">
              Manual correction required
            </span>
          </div>
          <p className="pt-1 text-sm font-medium text-amber-900">
            Suggested action: {warning.suggested_action}
          </p>
          {onResolveClick && (
            <div className="pt-2">
              <Button
                onClick={onResolveClick}
                className="w-full sm:w-auto bg-amber-600 hover:bg-amber-700 text-white"
                data-testid={`${testIdPrefix}-resolve-btn`}
              >
                <Wrench className="mr-2 h-4 w-4" />
                Log manual correction
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WarningBanner;
